import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Container,
  Heading,
  LevelBadge,
  Row,
  Screen,
  Stack,
  StreakFlame,
  Text,
  ThemeQuickToggle,
  XPBadge,
} from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { getCourseTree } from '../content/course.js';
import { buildProgressionView } from '../progress/progression.js';
import { useCompletions, useStreak, useTotalXp } from '../progress/hooks.js';
import { xpToLevel } from '../progress/level.js';
import { DailyGoalRing } from '../home/DailyGoalRing.js';
import { HomeSkeleton } from '../home/HomeSkeleton.js';
import { SkillTree } from '../home/SkillTree.js';
import { MissionsCard } from '../missions/MissionsCard.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import { preloadAudio } from '../audio/audioPlayer.js';
import { audioKeysOf } from '../lesson/audioKeys.js';
import { getLesson } from '../content/lessons.js';
import type { Exercise } from '@fluent/types';
import '../home/Home.css';

/**
 * Home — signed-in landing screen. Sticky top bar holds XP total +
 * streak flame + profile avatar; the page body is the serpentine
 * skill tree for the current course.
 */

/** Today in the user's tz (YYYY-MM-DD) — matches the SQL `v_today` pattern. */
function todayInTz(tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

function isStreakActiveToday(lastActiveDate: string | null | undefined, tz: string | null | undefined): boolean {
  if (!lastActiveDate) return false;
  return lastActiveDate === todayInTz(tz ?? 'Asia/Tbilisi');
}

/** Flatten every audio_key referenced by a list of exercises. Used by the
 * home pre-cache to warm the next 3 unlocked lessons' audio. */
function audioKeysFor(exercises: Exercise[]): string[] {
  return audioKeysOf(exercises);
}

export function Home() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const displayName = profile?.display_name ?? user?.email?.split('@')[0] ?? '';

  const totalXp = useTotalXp();
  const { data: completions = {}, isPlaceholderData: completionsPending } = useCompletions();
  const { data: streak, isLoading: streakLoading } = useStreak();
  const streakDays = streak?.current_streak ?? 0;
  const streakActive = isStreakActiveToday(streak?.last_active_date, profile?.timezone);
  // First-load skeleton: useStreak has no placeholderData, so streak is
  // undefined ONLY on the initial fetch (subsequent navigations within the
  // session hit the React Query cache and bypass this branch).
  const showSkeleton = streakLoading && streak === undefined;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = useMemo(() => {
    const tree = getCourseTree();
    if (!tree) return null;
    // The progression helper is keyed by a `LessonCompletion` shape from
    // Phase 6; adapt the Supabase rows to that shape without leaking
    // infrastructure types into the pure helper.
    const adapted: Record<string, { lessonId: string; xp: number; accuracy: number; perfect: boolean; completedAt: number }> = {};
    for (const [id, row] of Object.entries(completions)) {
      adapted[id] = {
        lessonId: row.lesson_id,
        xp: row.xp_earned,
        accuracy: row.accuracy,
        perfect: row.perfect,
        completedAt: Date.parse(row.completed_at) || Date.now(),
      };
    }
    return buildProgressionView(tree, adapted);
  }, [completions]);

  // PWA manifest shortcut: long-pressing the home-screen icon offers
  // "გაკვეთილი", which opens /?shortcut=lesson. Home is the only surface that
  // knows which lesson is next, so it is a param, not a route. The param is
  // consumed immediately so refresh and back don't re-trigger it.
  useEffect(() => {
    // Wait for the real completions: `placeholderData: {}` means the first
    // render always thinks lesson 1 is next, which would drop a learner who
    // is on unit 4 back at "გამარჯობა".
    if (searchParams.get('shortcut') !== 'lesson' || !view || completionsPending) return;
    setSearchParams({}, { replace: true });
    if (view.nextLessonId) navigate(`/learn/${view.nextLessonId}`, { replace: true });
  }, [searchParams, setSearchParams, view, navigate, completionsPending]);

  // Predictive pre-cache: when home loads online, fetch the audio for the
  // next 3 unlocked-but-not-complete lessons so a tap on any of them works
  // offline. Workbox's CacheFirst rule on `audio.fluent.ge` gives us free
  // persistence; preloadAudio warms the Web Audio buffer cache (fetch +
  // decode) and triggers the network fetch the SW intercepts.
  useEffect(() => {
    if (!view) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    let queued = 0;
    outer: for (const u of view.units) {
      for (const s of u.skills) {
        for (const n of s.lessons) {
          if (n.state === 'complete' || n.state === 'golden' || n.state === 'locked') continue;
          const lesson = getLesson(n.lessonId);
          if (!lesson) continue;
          for (const key of audioKeysFor(lesson.exercises)) preloadAudio(key);
          queued++;
          if (queued >= 3) break outer;
        }
      }
    }
  }, [view]);

  if (showSkeleton) {
    return <HomeSkeleton />;
  }

  return (
    <Screen>
      <header className="fluent-home__topbar">
        <Row gap={3} align="center">
          <LevelBadge level={xpToLevel(totalXp)} size="sm" />
          <XPBadge xp={totalXp} size="sm" />
          <StreakFlame days={streakDays} active={streakActive} size="sm" />
          <DailyGoalRing size={48} />
        </Row>
        <Row gap={2} align="center">
          <ThemeQuickToggle ariaLabel={t('theme.toggle_aria')} />
          <Link
            to="/profile"
            aria-label={t('profile.title')}
            style={{ textDecoration: 'none' }}
          >
            <Avatar size={36} name={displayName} src={profile?.avatar_url ?? undefined} />
          </Link>
        </Row>
      </header>

      <Container size="sm">
        <Stack gap={8} style={{ padding: 'var(--space-6, 24px) 0 calc(var(--space-16, 64px) + 88px + env(safe-area-inset-bottom))' }}>
          {view ? (
            <>
              <MissionsCard />
              <SkillTree view={view} />
            </>
          ) : (
            <Stack gap={4} style={{ padding: '64px 0', textAlign: 'center' }}>
              <Heading level={2}>{t('home.no_course_title')}</Heading>
              <Text tone="muted">{t('home.no_course_body')}</Text>
              <div>
                <Button variant="primary" size="md" onClick={() => window.location.reload()}>
                  {t('home.no_course_retry')}
                </Button>
              </div>
            </Stack>
          )}
        </Stack>
      </Container>
      <AppBottomNav />
    </Screen>
  );
}
